/**
 * NATIVE BRIDGE
 * =============
 * This web page runs *inside* the native app (a "WebView"). The app injects a
 * special object onto `window` so the page can ask the app to do native things
 * — like start a call, open a chat, or jump to the feed.
 *
 * - Android exposes  window.Android.navigate(url)
 * - iOS exposes      window.webkit.messageHandlers.onAction.postMessage(...)
 * - A plain browser (local dev) has neither — so we just log instead.
 *
 * The "redirections" in the PRD (direct call, chat, feed, profile, recharge…)
 * will all be triggered by calling `navigate()` with the right deeplink.
 */

/** True when the page is running inside the native app (has a bridge). */
export const isInApp: boolean = !!(window.Android || window.webkit?.messageHandlers)

/** True when the page is running inside the iOS native app. */
export const isIOS: boolean = !!window.webkit?.messageHandlers

/**
 * Ask the native app to navigate to a deeplink (e.g. start a call/chat, open
 * the feed, a host profile, or recharge). The app closes this WebView and
 * routes natively.
 *
 * IMPORTANT: call the bridge method directly on `window.Android`. Do NOT pull
 * it into a local variable first —
 *     const fn = window.Android.navigate; fn(url)   // ❌ throws on Android
 * Detaching an injected @JavascriptInterface method breaks it.
 */
/**
 * Deeplinks come straight from the feed (server-driven), so we don't fully trust
 * them. Reject empties and dangerous schemes (javascript:/data:/vbscript:/file:/
 * blob:) before handing the value to the privileged native bridge. This is a
 * conservative block, not a strict allow-list — real deeplinks are askk URLs
 * (e.g. https://askk.co.in/…); tighten to a host allow-list once the full set of
 * valid deeplink schemes is confirmed.
 */
function isSafeDeeplink(deeplink: string): boolean {
  if (typeof deeplink !== 'string' || deeplink.trim() === '') return false
  return !/^\s*(javascript|data|vbscript|file|blob):/i.test(deeplink)
}

export function navigate(deeplink: string): void {
  if (!isSafeDeeplink(deeplink)) {
    // eslint-disable-next-line no-console
    console.warn('[bridge] blocked unsafe deeplink:', deeplink)
    return
  }
  if (typeof window.Android?.navigate === 'function') {
    window.Android.navigate(deeplink) // Android
  } else if (window.webkit?.messageHandlers?.onAction?.postMessage) {
    window.webkit.messageHandlers.onAction.postMessage({ jsonObject: deeplink }) // iOS
  } else {
    // Plain browser (local dev): there's no app to navigate. Log so we can see
    // what *would* have happened.
    // eslint-disable-next-line no-console
    console.log('[bridge] No native bridge — navigate:', deeplink)
  }
}

/**
 * Ask the native app to close this WebView (go back). Android removed its own
 * back button, so the in-page back arrow drives this instead.
 *
 * Mechanism (confirmed against the Android JsInterface): post a `closeScreen`
 * action through the shared `onAction` bridge. iOS gets the same payload via its
 * webkit message handler; a plain browser falls back to history.
 */
export function goBack(): void {
  const action = JSON.stringify({ type: 'closeScreen' })
  if (typeof window.Android?.onAction === 'function') {
    window.Android.onAction(action) // Android
  } else if (window.webkit?.messageHandlers?.onAction?.postMessage) {
    window.webkit.messageHandlers.onAction.postMessage({ jsonObject: action }) // iOS
  } else {
    window.history.back() // plain browser (local dev)
  }
}
